<template>
  <SBaseInput
    v-bind="pickProps(props, useBaseInput.scoped())"
    :rules="[...inferredRules, ...(props.rules ?? [])]"
    v-on="reEmit(emit, useBaseInput.emits)"
  >
    <template #input="{ id, errorId, validation, aria }">
      <STextareaAtom
        v-bind="
          mergeReactive(
            pickProps(props, useTextareaAtom.scoped),
            validation.props,
            aria,
          )
        "
        :id
        v-on="
          mergeListeners(reEmit(emit, useTextareaAtom.emits), validation.on)
        "
      />
      <SCharacterCounter
        v-if="!errorId && showCharacterCounter"
        v-bind="pickProps(props, useCharacterCounter.scoped)"
      />
    </template>
    <template v-for="(_index, slotName) in $slots" #[slotName]="data">
      <slot :name="slotName" v-bind="data" />
    </template>
  </SBaseInput>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { UseCharacterCounterScoped } from "@/lib/components/logic/atoms/useCharacterCounter.ts";
import type {
  UseTextareaAtomEmit,
  UseTextareaAtomScoped,
} from "@/lib/components/logic/atoms/useTextareaAtom.ts";
import type {
  UseBaseInputEmit,
  UseBaseInputScoped,
} from "@/lib/components/logic/molecules/useBaseInput.ts";

import SCharacterCounter from "@/lib/components/atoms/character-counter/SCharacterCounter.vue";
import STextareaAtom from "@/lib/components/atoms/textarea/STextareaAtom.vue";
import * as useCharacterCounter from "@/lib/components/logic/atoms/useCharacterCounter.ts";
import * as useTextareaAtom from "@/lib/components/logic/atoms/useTextareaAtom.ts";
import * as useBaseInput from "@/lib/components/logic/molecules/useBaseInput.ts";
import SBaseInput from "@/lib/components/molecules/SBaseInput.vue";
import { pickProps, reEmit } from "@/lib/composables/componentComposable.ts";
import { mergeListeners, mergeReactive } from "@/lib/helpers/reactivity.ts";
import { inferTextInputRules } from "@/lib/validation/inferrables/textInputRules.ts";

const props = defineProps<
  UseBaseInputScoped<UseTextareaAtomScoped["modelValue"]> &
    UseCharacterCounterScoped &
    UseTextareaAtomScoped
>();
const emit = defineEmits<UseBaseInputEmit & UseTextareaAtomEmit>();

const inferredRules = inferTextInputRules(props);
const showCharacterCounter = computed(
  () => !!props.maxlength && props.characterCounter !== false,
);
</script>
